import {
  BattingCageCookie,
  ListenerTypes,
  activeHeartbeat,
  cloneDeep,
  isNumber,
  listener,
  setEventListener,
  store,
  waitForElement,
} from "../shared";
import {
  ITrigger,
  IBehaviorType,
  ITriggeredContentByPage,
  TriggerPageShortCodeAndZoneName,
  IEventLoggerEnum,
} from "../types";
import { EventLoggerModule } from "./event-logger";

import { TriggerContentModule } from "./trigger-content";
import { TriggerPreviewsModule } from "./trigger-previews";

export const TriggerEventsModule = (() => {
  const getTriggeredContentByPage = () => store.get("triggeredContentByPage");

  const updateBattingCageScriptCookie = (trigger: ITrigger) => {
    if (store.get("isPreviewMode")) {
      TriggerPreviewsModule.updateBattingCageScriptPreviewCookiePersistedExperience(
        trigger
      );
      return;
    }

    const persistedExperience = store.get("persistedExperience");

    const updatedPersistedExperience = cloneDeep(persistedExperience);

    const battingCageCookie = BattingCageCookie.get();

    /**
     * This should be the trigger.behavior that was fired (ie, if 3
     * behaviors and only clicked fired, only add the click one here)
     *
     */

    const newPersistedTrigger = {
      triggerId: trigger.triggerId,
      behaviorId: trigger.behaviorId,
      behaviorType: trigger.behaviorType,
      pageUrl: "",
      timestamp: new Date().toISOString(),
      utcTimestamp: new Date().toUTCString(),
    };

    updatedPersistedExperience.push(newPersistedTrigger);

    const updatedCooke = {
      ...battingCageCookie,
      persistedExperience: updatedPersistedExperience,
    };

    store.set("persistedExperience", updatedPersistedExperience);

    BattingCageCookie.set({ value: updatedCooke });
  };

  const logTriggerActivatedEvent = ({ triggerId, behaviorId }: ITrigger) => {
    EventLoggerModule.logEvent({
      eventType: IEventLoggerEnum.TriggerActivated,
      payload: {
        triggerId,
        behaviorId,
        triggerApplied: false,
        timeTriggered: new Date().toUTCString(),
      },
    });
  };

  const setTriggeredEventByPage = (trigger: ITrigger) => {
    const triggeredContentByPage = getTriggeredContentByPage();
    const universalTriggeredContent = store.get("universalTriggeredContent");

    const { triggerContent: triggersContent, triggerId, behaviorId } = trigger;

    let updatedTriggeredEventByPage = cloneDeep(triggeredContentByPage);
    let updatedUniversalTriggeredContent = cloneDeep(universalTriggeredContent);

    logTriggerActivatedEvent(trigger);

    /**
     * Instantiate triggeredContentByPage object and universalTriggeredContent array.
     *
     * If pageUrl is fake Universal, or no pageUrl and no pageShortCode, put content
     * into universalTriggeredContent array, else put into triggeredContentByPage object
     * with pageUrl as top level key and pageShortCode and zoneShortCode as nested key
     *
     */

    triggersContent.forEach((triggerContent) => {
      const { zoneShortCode, pageShortCode, pageUrl } = triggerContent;

      const currentTriggeredEventId = `${pageShortCode}.${zoneShortCode}`;

      const activatedContent = {
        ...triggerContent,
        triggerId,
        behaviorId,
        triggerApplied: false,
        timeTriggered: new Date().toUTCString(),
      };

      if (!pageUrl || !pageShortCode) {
        updatedUniversalTriggeredContent.push(activatedContent);
      } else {
        const currentPageOfTriggeredEvents =
          updatedTriggeredEventByPage[pageUrl] ||
          ({} as Record<
            TriggerPageShortCodeAndZoneName,
            ITriggeredContentByPage
          >);

        updatedTriggeredEventByPage = {
          ...updatedTriggeredEventByPage,
          [pageUrl]: {
            ...currentPageOfTriggeredEvents,
            [currentTriggeredEventId]: activatedContent,
          },
        };
      }
    });

    store.set("triggeredContentByPage", updatedTriggeredEventByPage);
    store.set("universalTriggeredContent", updatedUniversalTriggeredContent);

    updateBattingCageScriptCookie(trigger);
  };

  const checkIfTriggerAlreadyApplied = (trigger: ITrigger) => {
    const persistedExperience = store.get("persistedExperience");

    const hasAlreadyBeenApplied = persistedExperience.find(
      ({ behaviorId, triggerId }) =>
        behaviorId === trigger.behaviorId && triggerId === trigger.triggerId
    );

    return Boolean(hasAlreadyBeenApplied);
  };

  const EventHandlers = {
    click: (trigger: ITrigger) => (event: any) => {
      setTriggeredEventByPage(trigger);

      requestAnimationFrame(() => {
        TriggerContentModule.setPageContent();
      });
    },
    exitIntent: (trigger: ITrigger) => {
      const triggerAlreadyApplied = checkIfTriggerAlreadyApplied(trigger);

      if (triggerAlreadyApplied) return;
      checkIfTriggerAlreadyApplied(trigger);

      setTriggeredEventByPage(trigger);

      requestAnimationFrame(() => {
        TriggerContentModule.setPageContent();
      });
    },
    timeOnPage: (trigger: ITrigger) => {
      const triggerAlreadyApplied = checkIfTriggerAlreadyApplied(trigger);

      if (triggerAlreadyApplied) return;

      setTriggeredEventByPage(trigger);

      requestAnimationFrame(() => {
        TriggerContentModule.setPageContent();
      });
    },
    scrollPercentage: ({
      trigger,
      scrollPercentage,
    }: {
      trigger: ITrigger;
      scrollPercentage: number;
    }) => {
      const triggerAlreadyApplied = checkIfTriggerAlreadyApplied(trigger);

      if (triggerAlreadyApplied) return;

      const { behaviorParameter } = trigger;

      if (isNumber(behaviorParameter)) {
        if (scrollPercentage >= parseInt(behaviorParameter)) {
          setTriggeredEventByPage(trigger);

          requestAnimationFrame(() => {
            TriggerContentModule.setPageContent();
          });
        }
      }
    },
  };

  const setTriggerEventListenersOnCurrentPage = () => {
    const currentPageTriggers = store.get("currentPageTriggers");

    if (currentPageTriggers.length) {
      currentPageTriggers.forEach((pageTrigger) => {
        const {
          behaviorType,
          behaviorZoneCode,
          behaviorParameter,
          behaviorPageShortCode,
        } = pageTrigger;

        const selector = `[data-zone-short-code="${behaviorZoneCode}"]${
          behaviorPageShortCode
            ? `[data-page-short-code="${behaviorPageShortCode || ""}"]`
            : ""
        }`;

        const callBack = (element?: HTMLElement) => {
          if (!element) return;
          switch (behaviorType) {
            case IBehaviorType.TimeOnPage:
              if (checkIfTriggerAlreadyApplied(pageTrigger)) return;

              const milliseconds = parseInt(behaviorParameter) * 1000;

              const timerId = activeHeartbeat.set(
                () => EventHandlers.timeOnPage(pageTrigger),
                milliseconds
              );

              listener.on(ListenerTypes.urlChange, () => {
                activeHeartbeat.clearTimer(timerId);
              });

              break;
            case IBehaviorType.Click:
              setEventListener({
                selector,
                eventType: "click",
                callback: EventHandlers.click(pageTrigger),
              });

              break;
            case IBehaviorType.ExitIntent:
              listener.on(ListenerTypes.exitIntent, () => {
                if (checkIfTriggerAlreadyApplied(pageTrigger)) return;

                EventHandlers.exitIntent(pageTrigger);
              });

              break;
            case IBehaviorType.ScrollPercentage:
              listener.on(
                ListenerTypes.scrollPercentage,
                (scrollPercentage: number) => {
                  if (checkIfTriggerAlreadyApplied(pageTrigger)) return;
                  EventHandlers.scrollPercentage({
                    trigger: pageTrigger,
                    scrollPercentage,
                  });
                }
              );

              break;
            default:
              console.error("Unknown trigger type " + behaviorType);
          }
        };

        behaviorZoneCode ? waitForElement(selector, callBack) : callBack();
      });
    }
  };

  return {
    setTriggerEventListenersOnCurrentPage,
  };
})();
